/**
 * Settings → Security: my signed-in sessions (Better Auth sessions: browsers, the desktop app, the mobile apps), each
 * described by `logic/sessions` (device, OS, browser; "This device" first), with "Sign out" per session and "Sign out
 * of all other sessions". A revoked session can't refresh its access token any more; the 15-minute access token it
 * already holds runs out on its own (docs/auth/sign-in.md). For owners and admins also a short workspace-security
 * summary (who can invite, guest access, joining by email domain) linking to the Workspace page. Online only.
 */
import type { AccountSession } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isManager } from "../logic/roles.ts";
import { describeSession, sortSessions, type SessionDescription } from "../logic/sessions.ts";
import { errorKind } from "../ports.ts";
import type { SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type SessionRow = AccountSession & { description: SessionDescription };

export type SecuritySettingsState = {
  status: "loading" | "ready" | "error";
  /** This device first, then most recently active. */
  sessions: SessionRow[];
  /** Sessions other than this one. */
  otherCount: number;
  /** The session being signed out (its row shows a spinner). */
  revokingId?: string;
  revokingOthers: boolean;
  /** Asking "Sign out of all other sessions?". */
  confirmingOthers: boolean;
  /** Owner or admin here: the workspace section shows. */
  manager: boolean;
  /** Allowed email domains (managers; undefined until loaded or when unavailable). */
  domains?: string[];
  notice?: MessageDescriptor;
};

function failure(e: unknown): MessageDescriptor {
  return errorKind(e) === "network" ? msg("settings.offline") : msg("settings.failed");
}

export class SecuritySettingsViewModel extends ViewModel<SecuritySettingsState> {
  private readonly deps: SettingsDeps;
  readonly ws: WorkspaceViewModel;

  constructor(ws: WorkspaceViewModel, deps: SettingsDeps) {
    super({ status: "loading", sessions: [], otherCount: 0, revokingOthers: false, confirmingOthers: false, manager: false });
    this.ws = ws;
    this.deps = deps;
    this.onDispose(ws.subscribe(() => this.follow()));
    this.follow();
  }

  private follow(): void {
    const me = this.ws.state.cache?.members[this.ws.state.meId];
    const manager = me !== undefined && isManager(me, this.ws.deps.clock.now());
    if (manager !== this.state.manager) this.update({ manager });
  }

  private setSessions(list: AccountSession[]): void {
    const sessions = sortSessions(list).map((s) => ({ ...s, description: describeSession(s) }));
    this.update({ sessions, otherCount: sessions.filter((s) => !s.current).length });
  }

  async load(): Promise<void> {
    if (this.state.manager) void this.loadDomains();
    try {
      const sessions = await this.deps.api.sessions();
      if (this.isDisposed) return;
      this.setSessions(sessions);
      this.update({ status: "ready" });
    } catch {
      if (!this.isDisposed && this.state.status !== "ready") this.update({ status: "error" });
    }
  }

  private async loadDomains(): Promise<void> {
    try {
      const domains = await this.deps.api.emailDomains(this.ws.wsId);
      if (!this.isDisposed) this.update({ domains });
    } catch {
      // The summary just leaves the domains line out.
    }
  }

  /** Signs one other session out. */
  async revoke(id: string): Promise<void> {
    const row = this.state.sessions.find((s) => s.id === id);
    if (row === undefined || row.current || this.state.revokingId !== undefined) return;
    this.update({ revokingId: id });
    try {
      await this.deps.api.revokeSession(id);
      if (this.isDisposed) return;
      this.setSessions(this.state.sessions.filter((s) => s.id !== id));
      this.update({ notice: msg("security.sessions.revokedOne") });
    } catch (e) {
      if (this.isDisposed) return;
      // Already gone (expired or signed out elsewhere): drop it from the list.
      if (errorKind(e) === "notFound") this.setSessions(this.state.sessions.filter((s) => s.id !== id));
      else this.update({ notice: failure(e) });
    } finally {
      if (!this.isDisposed) this.update({ revokingId: undefined });
    }
  }

  askRevokeOthers(): void {
    if (this.state.otherCount > 0) this.update({ confirmingOthers: true });
  }

  cancelRevokeOthers(): void {
    if (!this.state.revokingOthers) this.update({ confirmingOthers: false });
  }

  async revokeOthers(): Promise<void> {
    if (this.state.revokingOthers) return;
    this.update({ revokingOthers: true });
    try {
      const n = await this.deps.api.revokeOtherSessions();
      if (this.isDisposed) return;
      this.setSessions(this.state.sessions.filter((s) => s.current));
      this.update({ confirmingOthers: false, notice: msg("account.sessions.revoked", { count: n }) });
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: failure(e) });
    } finally {
      if (!this.isDisposed) this.update({ revokingOthers: false });
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
